import {
    availableCalculatorGroupIds,
    calculatorItemLabel,
    calculatorFishingSourceTraces,
    effectiveCalculatorTraceLayer,
    orderedCalculatorLayerStack,
    selectedCalculatorTrace,
} from "./calculator-layer-inspector-contract.js";
import {
    calculatorFishingSourceLabel,
    calculatorFishingSourcesEqual,
} from "./calculator-fishing-source.js";
import {
    buildCalculatorRankingEvidenceRows,
    filterCalculatorRankingEvidenceRows,
} from "./calculator-data-evidence.js";
import {
    calculatorDataEnumLabel as enumLabel,
    calculatorDataText as t,
} from "./calculator-data-i18n.js";
import { parseCalculatorClaimDateV1 } from "../contracts/calculator-claim-date-v1.js";
import { presentCalculatorClaimDateV1 } from "./calculator-claim-date-presentation.js";
import {
    createCalculatorLootTableEditorRenderState,
    renderCalculatorLootTableEditorFromState,
} from "./calculator-loot-table-editor-view.js";
import {
    calculatorGroupRoleRankV1,
    isCalculatorGroupRoleV1,
} from "../calculator-group-role-v1.js";
import {
    createCalculatorLootProvenanceGraphState,
    renderCalculatorLootProvenanceGraph,
} from "./calculator-loot-provenance-graph.js";
import { parseCalculatorLootSubject } from "./calculator-loot-subject.js";

function escapeText(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function effectiveTraceOperation(trace) {
    return trace?.candidates?.find((candidate) => candidate.status === "effective")
        ?.operation ?? null;
}

function coherentRenderTrace(projection, traceId, fishingSource, selectedItemId) {
    if (!Number.isInteger(selectedItemId)) {
        return selectedCalculatorTrace(projection, traceId, fishingSource);
    }
    const traces = calculatorFishingSourceTraces(projection, fishingSource);
    const requested = traces.find((trace) => trace.traceId === traceId);
    if (requested?.factKey?.itemId === selectedItemId) {
        return requested;
    }
    return traces.find((trace) => (
        trace.factKey?.kind === "itemPresence"
        && trace.factKey.itemId === selectedItemId
    )) ?? traces.find((trace) => trace.factKey?.itemId === selectedItemId) ?? null;
}

export function calculatorLootEditorGroupIds(projection, fishingSource) {
    const groups = new Map();
    for (const trace of calculatorFishingSourceTraces(projection, fishingSource)) {
        const operation = effectiveTraceOperation(trace);
        if (
            trace.factKey.kind === "groupPresence"
            && operation
            && isCalculatorGroupRoleV1(operation.role)
        ) {
            groups.set(trace.factKey.groupId, operation.role);
        }
    }
    const presentationRanks = new Map((projection.presentationOrder.groups ?? []).map((entry) => [
        entry.groupId,
        entry.rank,
    ]));
    return Object.freeze([...groups]
        .sort(([leftId, leftRole], [rightId, rightRole]) => (
            calculatorGroupRoleRankV1(leftRole) - calculatorGroupRoleRankV1(rightRole)
            || (presentationRanks.get(leftId) ?? Number.MAX_SAFE_INTEGER)
                - (presentationRanks.get(rightId) ?? Number.MAX_SAFE_INTEGER)
            || leftId.localeCompare(rightId)
        ))
        .map(([groupId]) => groupId));
}

function presentedItemLabel(itemId, customCatalogItems, dataPresentation) {
    const label = dataPresentation?.itemsByItemId?.[String(itemId)]?.label;
    return typeof label === "string" && label.trim()
        ? label.trim()
        : calculatorItemLabel(itemId, customCatalogItems);
}

function presentedGroupLabel(groupId, dataPresentation) {
    const label = dataPresentation?.groupsByGroupId?.[groupId]?.label;
    return typeof label === "string" && label.trim() ? label.trim() : groupId;
}

function metadataRow(term, value) {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    return `<div class="flex min-w-0 gap-2"><dt class="font-medium">${escapeText(term)}</dt>`
        + `<dd class="min-w-0 break-all">${escapeText(value)}</dd></div>`;
}

function hashText(contentHash) {
    return contentHash ? `${contentHash.algorithm}:${contentHash.digest}` : null;
}

function layerMetadata(layer) {
    const provenance = layer.provenance;
    return [
        metadataRow(t("calculator.data.metadata.source_id", "Source ID"), provenance.sourceId),
        metadataRow(t("calculator.data.metadata.source_revision", "Source revision"), provenance.sourceRevision),
        metadataRow(t("calculator.data.metadata.content_hash", "Content hash"), hashText(provenance.contentHash)),
        metadataRow(t("calculator.data.metadata.imported", "Imported"), provenance.importedAtUtc),
    ].join("");
}

function layerAttachedSources(layer) {
    const operations = Array.isArray(layer.facts) ? layer.facts : layer.operations;
    const sources = new Map();
    for (const operation of operations) {
        const provenance = operation.provenance;
        if (!provenance?.sourceId) {
            continue;
        }
        const source = sources.get(provenance.sourceId) ?? {
            sourceId: provenance.sourceId,
            count: 0,
            tables: new Set(),
            sheets: new Set(),
        };
        source.count += 1;
        if (provenance.sourceTable) {
            source.tables.add(provenance.sourceTable);
        }
        if (provenance.sourceSheet) {
            source.sheets.add(provenance.sourceSheet);
        }
        sources.set(provenance.sourceId, source);
    }
    if (!sources.size) {
        return "";
    }
    const sourceCount = t(
        sources.size === 1
            ? "calculator.data.layer.loaded_sources.one"
            : "calculator.data.layer.loaded_sources.other",
        sources.size === 1
            ? "{ $count } loaded effective source"
            : "{ $count } loaded effective sources",
        { count: sources.size },
    );
    return '<details class="collapse collapse-arrow border border-base-300 bg-base-200">'
        + `<summary class="collapse-title min-h-0 px-3 py-2 text-xs font-medium">${escapeText(sourceCount)}</summary>`
        + '<ul class="collapse-content grid gap-2 px-3 pb-3 text-xs">'
        + [...sources.values()].map((source) => (
            '<li class="grid gap-1"><span class="break-all font-medium">'
            + escapeText(source.sourceId)
            + `</span><span class="opacity-60">${escapeText(t(
                source.count === 1
                    ? "calculator.data.operation_count.one"
                    : "calculator.data.operation_count.other",
                source.count === 1 ? "{ $count } operation" : "{ $count } operations",
                { count: source.count },
            ))}`
            + (source.tables.size ? ` · ${escapeText([...source.tables].join(", "))}` : "")
            + (source.sheets.size ? ` · ${escapeText([...source.sheets].join(", "))}` : "")
            + '</span></li>'
        )).join("")
        + '</ul></details>';
}

function renderLayerStack(model, userLayerDocument, effectiveLayerId) {
    const layers = orderedCalculatorLayerStack(model, userLayerDocument);
    if (!layers.length) {
        return `<p class="text-sm opacity-70">${escapeText(t("calculator.data.layer.no_model", "No model is loaded."))}</p>`;
    }
    return `<ol class="grid gap-3" data-layer-stack>${layers.map((layer) => {
        const effective = layer.layerId === effectiveLayerId;
        const operationCount = Array.isArray(layer.facts)
            ? layer.facts.length
            : layer.operations.length;
        return `<li class="card border border-base-300 bg-base-100" data-layer-id="${escapeText(layer.layerId)}">`
            + '<div class="card-body gap-2 p-4">'
            + '<div class="flex flex-wrap items-center gap-2">'
            + `<h4 class="card-title text-sm">${escapeText(layer.name)}</h4>`
            + `<span class="badge badge-outline">${escapeText(t("calculator.data.layer.order", "Order { $order }", { order: layer.order }))}</span>`
            + `<span class="badge">${escapeText(enumLabel(layer.evidenceClass))}</span>`
            + (effective ? `<span class="badge badge-success badge-outline">${escapeText(t("calculator.data.layer.effective_for_fact", "Effective for selected fact"))}</span>` : "")
            + (layer.readOnly
                ? `<span class="badge badge-ghost" data-layer-read-only>${escapeText(t("calculator.data.layer.read_only", "Read-only"))}</span>`
                : `<span class="badge badge-primary badge-outline" data-layer-editable>${escapeText(t("calculator.data.layer.user_editable", "User-editable"))}</span>`)
            + '</div>'
            + `<p class="text-xs opacity-70">${escapeText(t(
                operationCount === 1
                    ? "calculator.data.fact_count.one"
                    : "calculator.data.fact_count.other",
                operationCount === 1 ? "{ $count } fact operation" : "{ $count } fact operations",
                { count: operationCount },
            ))}</p>`
            + `<dl class="grid gap-1 text-xs">${layerMetadata(layer)}</dl>`
            + layerAttachedSources(layer)
            + '</div></li>';
    }).join("")}</ol>`;
}

function factKeyParts(factKey, customCatalogItems, dataPresentation) {
    const parts = [enumLabel(factKey.kind)];
    if (Number.isInteger(factKey.itemId)) {
        parts.push(presentedItemLabel(factKey.itemId, customCatalogItems, dataPresentation));
    }
    if (factKey.zoneId) {
        parts.push(t("calculator.data.fact.zone", "zone { $id }", { id: factKey.zoneId }));
    }
    if (factKey.fishingSource) {
        parts.push(calculatorFishingSourceLabel(
            factKey.fishingSource,
            "projection.provenanceTrace.factKey.fishingSource",
        ));
    }
    if (factKey.groupId) {
        parts.push(t("calculator.data.fact.group", "group { $id }", { id: factKey.groupId }));
    }
    if (factKey.branchId) {
        parts.push(t("calculator.data.fact.branch", "branch { $id }", { id: factKey.branchId }));
    }
    if (factKey.targetFactId) {
        parts.push(t("calculator.data.fact.fact", "fact { $id }", { id: factKey.targetFactId }));
    }
    return parts;
}

export function calculatorTraceLabel(trace, customCatalogItems = [], dataPresentation = null) {
    return factKeyParts(trace.factKey, customCatalogItems, dataPresentation).join(" · ");
}

function selectedOperation(trace) {
    return trace?.candidates.find((candidate) => candidate.status === "effective")?.operation
        ?? trace?.candidates.at(-1)?.operation
        ?? null;
}

function selectedOption(value, expected) {
    return value === expected ? " selected" : "";
}

function groupOptions(groupIds, selectedGroupId = "", dataPresentation = null) {
    return groupIds.map((groupId) => (
        `<option value="${escapeText(groupId)}"${selectedOption(groupId, selectedGroupId)}>${escapeText(presentedGroupLabel(groupId, dataPresentation))} · ${escapeText(groupId)}</option>`
    )).join("");
}

function relatedItemTrace(projection, fishingSource, kind, itemId, predicate = () => true) {
    return calculatorFishingSourceTraces(projection, fishingSource).find((candidateTrace) => (
        candidateTrace.factKey.kind === kind
        && candidateTrace.factKey.itemId === itemId
        && predicate(candidateTrace)
    )) ?? null;
}

function itemMemberships(projection, fishingSource, itemId, dataPresentation) {
    return calculatorFishingSourceTraces(projection, fishingSource).flatMap((candidateTrace) => {
        if (
            candidateTrace.factKey.kind !== "groupMembership"
            || candidateTrace.factKey.itemId !== itemId
        ) {
            return [];
        }
        const candidate = candidateTrace.candidates.find((entry) => entry.status === "effective");
        if (candidate?.operation?.membership !== "member") {
            return [];
        }
        return [{
            groupId: candidateTrace.factKey.groupId,
            groupLabel: presentedGroupLabel(candidateTrace.factKey.groupId, dataPresentation),
            layerName: candidate.layer.name,
            traceId: candidateTrace.traceId,
        }];
    });
}

function renderItemEditors(
    model,
    projection,
    trace,
    groupIds,
    dataPresentation,
    customCatalogItems,
) {
    const factKey = trace?.factKey;
    const itemId = factKey?.itemId;
    if (!Number.isInteger(itemId)) {
        return "";
    }
    const operation = selectedOperation(trace);
    const fishingSource = factKey.fishingSource ?? model.selection.fishingSource;
    const rankedItem = projection.presentationOrder.items.find((item) => item.itemId === itemId);
    const groupId = factKey.groupId
        ?? operation?.groupId
        ?? rankedItem?.groupId
        ?? groupIds[0]
        ?? "";
    const presenceOperation = selectedOperation(relatedItemTrace(
        projection,
        fishingSource,
        "itemPresence",
        itemId,
    ));
    const presence = presenceOperation?.presence ?? "unknown";
    const memberships = itemMemberships(projection, fishingSource, itemId, dataPresentation);
    const membershipBadges = memberships.length
        ? memberships.map((membership) => (
            `<button class="badge badge-outline h-auto gap-1 py-1" type="button" data-action="select-loot-row" data-group-id="${escapeText(membership.groupId)}" data-item-id="${itemId}" data-trace-id="${escapeText(membership.traceId)}">`
            + `<span>${escapeText(membership.groupLabel)}</span>`
            + `<span class="opacity-60">${escapeText(t("calculator.data.assignment.via_layer", "via { $layer }", { layer: membership.layerName }))}</span></button>`
        )).join("")
        : `<span class="badge badge-warning badge-outline">${escapeText(t("calculator.data.assignment.no_memberships", "No effective group memberships"))}</span>`;
    return '<section class="card border border-primary/30 bg-base-100" aria-labelledby="calculator-layer-user-edit-heading">'
        + '<div class="card-body gap-4 p-4">'
        + `<div><h4 class="card-title text-sm" id="calculator-layer-user-edit-heading">${escapeText(t("calculator.data.advanced.user_layer_edit", "User-layer edit"))}</h4>`
        + `<p class="text-xs opacity-70">${escapeText(t("calculator.data.advanced.editing_item", "Editing { $item }. Presence and each group membership are independent typed facts.", { item: presentedItemLabel(itemId, customCatalogItems, dataPresentation) }))}</p></div>`
        + `<div class="flex flex-wrap gap-2" data-effective-memberships>${membershipBadges}</div>`
        + '<div class="grid gap-3 sm:grid-cols-2">'
        + `<label class="fieldset"><span class="label">${escapeText(t("calculator.data.field.presence", "Presence"))}</span>`
        + '<select class="select select-sm" data-edit-presence>'
        + `<option value="present"${selectedOption(presence, "present")}>${escapeText(enumLabel("present"))}</option>`
        + `<option value="absent"${selectedOption(presence, "absent")}>${escapeText(enumLabel("absent"))}</option>`
        + `<option value="unknown"${selectedOption(presence, "unknown")}>${escapeText(enumLabel("unknown"))}</option>`
        + '</select></label>'
        + `<div class="flex items-end"><button class="btn btn-primary btn-sm" type="button" data-action="set-item-presence">${escapeText(t("calculator.data.action.apply_presence", "Apply presence"))}</button></div>`
        + (groupIds.length
            ? `<label class="fieldset"><span class="label">${escapeText(t("calculator.data.field.membership_group", "Membership group"))}</span>`
                + `<select class="select select-sm" data-edit-membership-group>${groupOptions(groupIds, groupId, dataPresentation)}</select></label>`
                + `<label class="fieldset"><span class="label">${escapeText(t("calculator.data.field.membership_state", "Membership state"))}</span>`
                + '<select class="select select-sm" data-edit-membership-state>'
                + `<option value="member">${escapeText(t("calculator.data.membership.member", "Included in group"))}</option>`
                + `<option value="notMember">${escapeText(t("calculator.data.membership.not_member", "Excluded from group"))}</option>`
                + `<option value="unknown">${escapeText(t("calculator.data.membership.unknown", "Membership unknown"))}</option>`
                + '</select></label>'
                + `<div class="flex items-end sm:col-span-2"><button class="btn btn-primary btn-sm" type="button" data-action="set-item-group-membership">${escapeText(t("calculator.data.action.apply_membership", "Apply group membership"))}</button></div>`
            : "")
        + '</div>'
        + `<input type="hidden" data-edit-item-id value="${itemId}">`
        + `<input type="hidden" data-edit-current-group value="${escapeText(groupId)}">`
        + '</div></section>';
}

function rankingSourceIdentity(sourceName, sourceId) {
    const name = String(sourceName ?? "").trim();
    const id = String(sourceId ?? "").trim();
    if (name && id && name !== id) {
        return `${name} · ${id}`;
    }
    return name || id;
}

function renderRankingObservation(observation) {
    const effective = observation.status === "effective";
    const evidenceClass = effective
        ? (observation.tone === "success" ? "badge-success" : "badge-warning")
        : "badge-ghost";
    const sourceIdentity = rankingSourceIdentity(
        observation.sourceName,
        observation.sourceId,
    );
    return `<li class="grid gap-1 rounded-field border border-base-300 p-2${effective ? "" : " opacity-60"}" data-ranking-candidate-status="${escapeText(observation.status)}">`
        + '<span class="flex flex-wrap gap-1">'
        + `<span class="badge ${evidenceClass} badge-outline h-auto justify-start py-1">${escapeText(observation.label)} ×${observation.count}</span>`
        + `<span class="badge badge-outline">${escapeText(enumLabel(observation.status))}</span>`
        + '</span>'
        + (observation.observedAtUtc
            ? `<span class="text-xs opacity-70">${escapeText(t("calculator.data.evidence.observed_at", "Observed { $time }", { time: observation.observedAtUtc }))}</span>`
            : "")
        + `<span class="text-xs opacity-70">${sourceIdentity ? `${escapeText(sourceIdentity)} · ` : ""}${escapeText(observation.layerName)} · ${escapeText(t(
            observation.factIds.length === 1
                ? "calculator.data.evidence.candidate_count.one"
                : "calculator.data.evidence.candidate_count.other",
            observation.factIds.length === 1
                ? "{ $count } candidate fact"
                : "{ $count } candidate facts",
            { count: observation.factIds.length },
        ))}</span>`
        + '</li>';
}

function renderRankingAssignments(row) {
    if (!row.assignments.length) {
        return `<div class="grid gap-1"><span class="badge badge-warning badge-outline">${escapeText(t("calculator.data.assignment.unassigned", "Unassigned"))}</span>`
            + `<span class="text-xs opacity-60">${escapeText(t("calculator.data.evidence.unassigned_help", "Ranking supports zone presence only; choose one or more groups."))}</span></div>`;
    }
    return `<div class="grid gap-2">${row.assignments.map((assignment) => {
        const sourceIdentity = rankingSourceIdentity(
            assignment.sourceName,
            assignment.sourceId,
        );
        return `<button class="btn btn-ghost h-auto min-h-0 justify-start border border-base-300 px-2 py-2 text-left" type="button" aria-label="${escapeText(t("calculator.data.assignment.review_label", "Review { $group } assignment for { $item }", { group: assignment.groupLabel, item: row.label }))}" data-action="select-loot-row" data-group-id="${escapeText(assignment.groupId)}" data-item-id="${row.itemId}" data-trace-id="${escapeText(assignment.traceId)}">`
            + '<span class="grid min-w-0 gap-1">'
            + `<span><span class="badge badge-primary badge-outline">${escapeText(assignment.groupLabel)}</span> <span class="badge badge-ghost">${escapeText(t("calculator.data.rate.state_label", "{ $state } rate", { state: enumLabel(assignment.rate.state) }))}</span></span>`
            + `<span class="truncate text-xs opacity-60">${sourceIdentity ? `${escapeText(sourceIdentity)} · ` : ""}${escapeText(t("calculator.data.assignment.independent_fact", "Independent group fact from { $layer }", { layer: assignment.layerName }))}</span>`
            + '</span></button>';
    }).join("")}</div>`;
}

function rankingEvidencePresentation(rows, query) {
    const filtered = filterCalculatorRankingEvidenceRows(rows, query);
    const visible = filtered.slice(0, 120);
    const body = visible.length
        ? visible.map((row) => (
            `<tr data-ranking-evidence-item="${row.itemId}" data-ranking-assigned="${row.assigned}">`
            + '<td><div class="flex min-w-48 items-center gap-3">'
            + '<span class="grid size-10 place-items-center rounded-field bg-base-200 text-xs opacity-60" aria-hidden="true">ID</span>'
            + `<div><strong>${escapeText(row.label)}</strong><div class="text-xs opacity-60">${escapeText(t("calculator.data.item_id", "Item { $id }", { id: row.itemId }))}</div></div>`
            + '</div></td>'
            + `<td><ol class="grid min-w-64 gap-2">${row.observations.map(renderRankingObservation).join("")}</ol></td>`
            + `<td><div class="min-w-64">${renderRankingAssignments(row)}</div></td>`
            + '<td class="text-right">'
            + `<button class="btn btn-outline btn-sm" type="button" aria-label="${escapeText(t("calculator.data.evidence.review_item_label", "Review { $item } evidence and assignments", { item: row.label }))}" data-action="review-ranking-item" data-item-id="${row.itemId}"${row.presenceTraceId ? ` data-trace-id="${escapeText(row.presenceTraceId)}"` : " disabled"}>${escapeText(t("calculator.data.action.review_item", "Review item"))}</button>`
            + '</td></tr>'
        )).join("")
        : `<tr><td colspan="4"><div class="alert">${escapeText(t("calculator.data.evidence.no_matches", "No ranking evidence matches this filter."))}</div></td></tr>`;
    const summary = filtered.length > visible.length
        ? `<p class="text-xs opacity-70">${escapeText(t("calculator.data.evidence.showing_limit", "Showing the first { $visible } of { $total } matches. Refine the filter to inspect the rest.", { visible: visible.length, total: filtered.length }))}</p>`
        : `<p class="text-xs opacity-70">${escapeText(t(
            filtered.length === 1
                ? "calculator.data.evidence.zone_count.one"
                : "calculator.data.evidence.zone_count.other",
            filtered.length === 1
                ? "{ $count } ranking-supported item for this zone."
                : "{ $count } ranking-supported items for this zone.",
            { count: filtered.length },
        ))}</p>`;
    return Object.freeze({ body, summary });
}

function renderRankingEvidence(rows, query) {
    const presentation = rankingEvidencePresentation(rows, query);
    return '<section class="card border border-base-300 bg-base-100" aria-labelledby="calculator-ranking-evidence-heading">'
        + '<div class="card-body gap-4 p-4">'
        + '<div class="flex flex-wrap items-start justify-between gap-3"><div>'
        + `<h3 class="card-title text-base" id="calculator-ranking-evidence-heading">${escapeText(t("calculator.data.evidence.title", "Ranking evidence & group assignments"))}</h3>`
        + `<p class="text-sm opacity-70">${escapeText(t("calculator.data.evidence.description", "Spatial ranking observations support item presence in the selected zone. Group assignments are separate facts and may include several groups."))}</p>`
        + '</div><div class="join w-full max-w-md"><label class="input input-bordered input-sm join-item flex grow items-center gap-2">'
        + `<span class="text-xs opacity-60">${escapeText(t("calculator.data.field.filter", "Filter"))}</span>`
        + `<input class="grow" type="search" value="${escapeText(query)}" placeholder="${escapeText(t("calculator.data.search.filter_placeholder", "Item, ID, group, layer, or status"))}" data-ranking-evidence-search>`
        + `</label><button class="btn btn-outline btn-sm join-item" type="button" data-action="filter-ranking-evidence">${escapeText(t("calculator.data.action.apply", "Apply"))}</button></div></div>`
        + `<div class="alert alert-info py-2 text-sm"><span>${escapeText(t("calculator.data.evidence.rate_warning", "Observation counts are evidence of zone presence, never drop-rate estimates."))}</span></div>`
        + `<div data-layer-inspector-ranking-summary>${presentation.summary}</div>`
        + '<div class="overflow-x-auto"><table class="table table-zebra table-sm">'
        + `<caption class="sr-only">${escapeText(t("calculator.data.evidence.table_caption", "Ranking presence observations and the independent effective group assignments for each item"))}</caption>`
        + `<thead><tr><th>${escapeText(t("calculator.data.table.item", "Item"))}</th><th>${escapeText(t("calculator.data.table.zone_presence", "Zone presence evidence"))}</th><th>${escapeText(t("calculator.data.table.group_facts", "Effective group facts"))}</th><th><span class="sr-only">${escapeText(t("calculator.data.table.actions", "Actions"))}</span></th></tr></thead>`
        + `<tbody data-layer-inspector-ranking-body>${presentation.body}</tbody></table></div>`
        + '</div></section>';
}

function userOperationSummary(operation, customCatalogItems, dataPresentation) {
    const parts = [enumLabel(operation.kind)];
    if (Number.isInteger(operation.itemId)) {
        parts.push(presentedItemLabel(operation.itemId, customCatalogItems, dataPresentation));
    }
    if (operation.groupId) {
        parts.push(presentedGroupLabel(operation.groupId, dataPresentation));
    }
    if (operation.optionId) {
        parts.push(operation.optionId);
    }
    if (operation.targetFactId) {
        parts.push(operation.targetFactId);
    }
    return parts.join(" · ");
}

function renderUserOperationRows(operations, customCatalogItems, dataPresentation, empty) {
    return operations.length
        ? operations.map((operation) => {
            const summary = userOperationSummary(
                operation,
                customCatalogItems,
                dataPresentation,
            );
            const operationId = escapeText(operation.operationId);
            const comment = operation.provenance?.comment ?? "";
            const confidence = operation.provenance?.confidence ?? "guessed";
            const author = operation.provenance?.author;
            const createdAtUtc = operation.provenance?.createdAtUtc ?? "";
            const claimDate = operation.provenance?.claimDate
                ? presentCalculatorClaimDateV1(parseCalculatorClaimDateV1(
                    operation.provenance.claimDate,
                    `operation:${operation.operationId}.provenance.claimDate`,
                ))
                : "";
            const attribution = [
                author?.handle,
                createdAtUtc,
                claimDate
                    ? `${t("calculator.data.metadata.claim_date", "Claim date")}: ${claimDate}`
                    : null,
            ].filter(Boolean).join(" · ");
            return '<li class="grid gap-2 border-b border-base-300 py-3 last:border-b-0">'
                + '<div class="flex flex-wrap items-center justify-between gap-3">'
                + '<div class="min-w-0"><p class="font-medium">'
                + escapeText(summary)
                + `</p><p class="truncate text-xs opacity-60">${operationId}</p>`
                + (attribution
                    ? `<p class="break-words text-xs opacity-60">${escapeText(attribution)}</p>`
                    : "")
                + '</div>'
                + `<button class="btn btn-ghost btn-xs" type="button" aria-label="${escapeText(t("calculator.data.action.revert_label", "Revert { $operation }", { operation: summary }))}" data-action="revert-user-operation" data-operation-id="${operationId}">${escapeText(t("calculator.data.action.revert", "Revert"))}</button>`
                + '</div>'
                + `<details class="grid gap-2" data-operation-metadata-editor="${operationId}">`
                + `<summary class="btn btn-ghost btn-xs w-fit">${escapeText(t("calculator.data.field.operation_context", "Confidence & note"))}</summary>`
                + '<fieldset class="fieldset grid gap-2 pt-2 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start">'
                + `<label class="label grid gap-1"><span>${escapeText(t("calculator.data.field.confidence", "Confidence"))}</span>`
                + `<select class="select select-xs w-full" aria-label="${escapeText(t("calculator.data.field.confidence", "Confidence"))}" data-operation-confidence-for="${operationId}">`
                + `<option value="guessed"${selectedOption(confidence, "guessed")}>${escapeText(enumLabel("guessed"))}</option>`
                + `<option value="known"${selectedOption(confidence, "known")}>${escapeText(enumLabel("known"))}</option>`
                + '</select></label>'
                + `<label class="label grid gap-1"><span>${escapeText(t("calculator.data.field.operation_comment", "Operation comment"))}</span>`
                + `<textarea class="textarea textarea-xs min-h-14 w-full" maxlength="2000" rows="2" aria-label="${escapeText(t("calculator.data.field.operation_comment", "Operation comment"))}" placeholder="${escapeText(t("calculator.data.placeholder.operation_comment", "Why this override exists, what evidence supports it, or what still needs review"))}" data-operation-comment-for="${operationId}">${escapeText(comment)}</textarea></label>`
                + '</fieldset></details>'
                + '</li>';
        }).join("")
        : `<li class="py-4 text-sm opacity-70">${escapeText(empty)}</li>`;
}

function renderUserOperations(
    userLayerDocument,
    fishingSource,
    customCatalogItems,
    dataPresentation,
) {
    const operations = userLayerDocument.userLayer.operations;
    const current = operations.filter((operation) => (
        calculatorFishingSourcesEqual(operation.fishingSource, fishingSource)
    ));
    const global = operations.filter((operation) => operation.fishingSource === undefined);
    const otherSourceCount = operations.length - current.length - global.length;
    const sourceLabel = calculatorFishingSourceLabel(
        fishingSource,
        "model.selection.fishingSource",
    );
    const rows = renderUserOperationRows(
        current,
        customCatalogItems,
        dataPresentation,
        t("calculator.data.operations.none_source", "No personal operations for this fishing source yet. Review evidence or add a catalog item to begin."),
    );
    const globalRows = renderUserOperationRows(
        global,
        customCatalogItems,
        dataPresentation,
        t("calculator.data.operations.none_global", "No global personal operations."),
    );
    return '<section class="card border border-primary/30 bg-base-100" aria-labelledby="calculator-user-operations-heading">'
        + '<div class="card-body gap-3 p-4">'
        + '<div class="flex flex-wrap items-center justify-between gap-2"><div>'
        + `<h3 class="card-title text-base" id="calculator-user-operations-heading">${escapeText(t("calculator.data.operations.title", "Personal overlay operations"))}</h3>`
        + `<p class="text-sm opacity-70">${escapeText(t("calculator.data.operations.description", "Only this layer is editable. Showing operations scoped to { $source }; reverting reveals the next effective candidate.", { source: sourceLabel }))}</p>`
        + `</div><span class="badge badge-primary badge-outline">${escapeText(t(
            current.length === 1
                ? "calculator.data.operations.source_count.one"
                : "calculator.data.operations.source_count.other",
            current.length === 1 ? "{ $count } source operation" : "{ $count } source operations",
            { count: current.length },
        ))}</span></div>`
        + `<ul data-user-operation-list data-operation-scope="source">${rows}</ul>`
        + '<details class="collapse collapse-arrow border border-base-300 bg-base-200">'
        + `<summary class="collapse-title py-2 text-sm font-semibold">${escapeText(t("calculator.data.operations.global_count", "Global operations · { $count }", { count: global.length }))}</summary>`
        + `<ul class="collapse-content" data-user-operation-list data-operation-scope="global">${globalRows}</ul>`
        + '</details>'
        + (otherSourceCount
            ? `<p class="text-xs opacity-60">${escapeText(t(
                otherSourceCount === 1
                    ? "calculator.data.operations.hidden_source_count.one"
                    : "calculator.data.operations.hidden_source_count.other",
                otherSourceCount === 1
                    ? "{ $count } operation for another fishing source hidden."
                    : "{ $count } operations for other fishing sources hidden.",
                { count: otherSourceCount },
            ))}</p>`
            : "")
        + '</div></section>';
}

function rankingEvidenceRenderKey(rows) {
    return JSON.stringify(rows.map((row) => ({
        itemId: row.itemId,
        label: row.label,
        presenceTraceId: row.presenceTraceId,
        observations: row.observations,
        assignments: row.assignments.map((assignment) => ({
            groupId: assignment.groupId,
            groupLabel: assignment.groupLabel,
            traceId: assignment.traceId,
            layerName: assignment.layerName,
            sourceId: assignment.sourceId,
            sourceName: assignment.sourceName,
            rateState: assignment.rate.state,
        })),
        assigned: row.assigned,
    })));
}

const calculatorLayerInspectorRenderStates = new WeakSet();

function calculatorLayerInspectorRenderState(value) {
    if (!calculatorLayerInspectorRenderStates.has(value)) {
        throw new TypeError(
            "calculator layer-inspector render state must come from createCalculatorLayerInspectorRenderState()",
        );
    }
    return value;
}

export function createCalculatorLayerInspectorRenderState({
    model,
    projection,
    userLayerDocument,
    selectedTraceId = null,
    customCatalogItems = [],
    dataPresentation = null,
    lootSort,
    lootRateDisplayMode = "normalized",
    rankingEvidenceQuery = "",
    catalogSearchUrl = "",
    selectedItemId = null,
    itemFactsEditorItemId = null,
    selectedLootSubject = null,
    modelLoading = false,
    rateEditor = null,
    errorMessage = "",
    announcement = "",
}) {
    if (!model || !projection || !userLayerDocument) {
        throw new TypeError(
            "calculator layer-inspector render state requires model, projection, and user layer",
        );
    }
    const trace = coherentRenderTrace(
        projection,
        selectedTraceId,
        model.selection.fishingSource,
        selectedItemId,
    );
    const lootSubject = parseCalculatorLootSubject(
        selectedLootSubject,
        "selectedLootSubject",
    );
    const effectiveLayerId = effectiveCalculatorTraceLayer(trace)?.layerId ?? null;
    const groupIds = availableCalculatorGroupIds(model, projection);
    const editorGroupIds = calculatorLootEditorGroupIds(
        projection,
        model.selection.fishingSource,
    );
    const rankingEvidenceRows = buildCalculatorRankingEvidenceRows({
        model,
        projection,
        presentation: dataPresentation,
    });
    const advancedEditor = renderItemEditors(
        model,
        projection,
        trace,
        groupIds,
        dataPresentation,
        customCatalogItems,
    );
    const error = errorMessage
        ? `<div class="alert alert-error" role="alert" data-layer-inspector-error><span>${escapeText(errorMessage)}</span></div>`
        : "";
    const editor = createCalculatorLootTableEditorRenderState({
        projection,
        fishingSource: model.selection.fishingSource,
        groupIds: editorGroupIds,
        customCatalogItems,
        dataPresentation,
        lootSort,
        lootRateDisplayMode,
        catalogSearchUrl,
        selectedItemId,
        itemFactsEditorItemId,
        selectedLootSubject: lootSubject,
        modelLoading,
        rateEditor,
        rankingEvidenceRows,
        userLayerOperations: userLayerDocument.userLayer.operations,
    });
    const provenanceGraph = createCalculatorLootProvenanceGraphState({
        projection,
        provenanceResolver: editor.provenanceResolver,
        fishingSource: model.selection.fishingSource,
        selectedLootSubject: lootSubject,
        customCatalogItems,
        dataPresentation,
    });
    const state = Object.freeze({
        model,
        projection,
        userLayerDocument,
        selectedTraceId: trace?.traceId ?? null,
        selectedLootSubject: lootSubject,
        customCatalogItems,
        dataPresentation,
        rankingEvidenceQuery,
        announcement,
        error,
        effectiveLayerId,
        advancedEditor,
        rankingEvidenceRows,
        rankingEvidenceKey: rankingEvidenceRenderKey(rankingEvidenceRows),
        editor,
        provenanceGraph,
        provenanceGraphKey: provenanceGraph.renderKey,
    });
    calculatorLayerInspectorRenderStates.add(state);
    return state;
}

export function renderCalculatorLayerInspectorRankingPresentation(value) {
    const state = calculatorLayerInspectorRenderState(value);
    return rankingEvidencePresentation(
        state.rankingEvidenceRows,
        state.rankingEvidenceQuery,
    );
}

export function renderCalculatorLayerInspectorRegion(value, region) {
    const state = calculatorLayerInspectorRenderState(value);
    switch (region) {
    case "provenance":
        return renderCalculatorLootProvenanceGraph(state.provenanceGraph);
    case "ranking":
        return '<details class="collapse collapse-arrow border border-base-300 bg-base-100">'
            + `<summary class="collapse-title font-semibold">${escapeText(t("calculator.data.evidence.details_heading", "Ranking evidence details"))}</summary>`
            + '<div class="collapse-content">'
            + renderRankingEvidence(
                state.rankingEvidenceRows,
                state.rankingEvidenceQuery,
            )
            + '</div></details>';
    case "layers":
        return '<section class="card border border-base-300 bg-base-100" aria-labelledby="calculator-layer-stack-heading">'
            + `<div class="card-body gap-3 p-4"><div><h3 class="card-title text-base" id="calculator-layer-stack-heading">${escapeText(t("calculator.data.layer.current_source", "Current fishing-source layers"))}</h3>`
            + `<p class="text-sm opacity-70">${escapeText(t("calculator.data.layer.description", "Resolution runs from immutable base facts through ordered sources, then your layer last."))}</p></div>`
            + renderLayerStack(
                state.model,
                state.userLayerDocument,
                state.effectiveLayerId,
            )
            + '</div></section>';
    case "operations":
        return renderUserOperations(
            state.userLayerDocument,
            state.model.selection.fishingSource,
            state.customCatalogItems,
            state.dataPresentation,
        );
    case "advanced":
        return state.advancedEditor
            ? '<details class="collapse collapse-arrow border border-base-300 bg-base-100">'
                + `<summary class="collapse-title font-semibold">${escapeText(t("calculator.data.advanced.heading", "Advanced typed fact editor"))}</summary>`
                + '<div class="collapse-content">'
                + state.advancedEditor
                + '</div></details>'
            : "";
    default:
        throw new TypeError(`unsupported calculator layer-inspector region: ${region}`);
    }
}

function regionMarkup(state, region) {
    return `<div data-layer-inspector-region="${region}">`
        + renderCalculatorLayerInspectorRegion(state, region)
        + '</div>';
}

export function renderCalculatorLayerInspectorMarkup({
    provenanceDetailBindings = null,
    ...input
}) {
    if (!input.model || !input.projection || !input.userLayerDocument) {
        return '<section class="alert" role="status" data-layer-inspector-empty>'
            + `<span>${escapeText(t("calculator.data.empty", "The calculator model, projection, and finalized user layer are required."))}</span>`
            + '</section>';
    }
    const state = createCalculatorLayerInspectorRenderState(input);
    return renderCalculatorLayerInspectorFromState(state, {
        provenanceDetailBindings,
    });
}

export function renderCalculatorLayerInspectorFromState(
    value,
    { provenanceDetailBindings = null } = {},
) {
    const state = calculatorLayerInspectorRenderState(value);
    return '<section class="grid gap-5" aria-labelledby="calculator-layer-inspector-heading">'
        + `<div><h2 class="text-xl font-semibold" id="calculator-layer-inspector-heading">${escapeText(t("calculator.data.title", "Zone data"))}</h2>`
        + `<p class="text-sm opacity-70">${escapeText(t("calculator.data.description", "Edit the effective loot table visually, then inspect its exact source candidates when needed."))}</p></div>`
        + `<p class="sr-only" role="status" aria-live="polite" aria-atomic="true" data-layer-inspector-announcement>${escapeText(state.announcement)}</p>`
        + state.error
        + renderCalculatorLootTableEditorFromState(state.editor, {
            provenanceDetailBindings,
        })
        + regionMarkup(state, "provenance")
        + regionMarkup(state, "ranking")
        + '<div class="grid gap-5 xl:grid-cols-[minmax(18rem,0.8fr)_minmax(28rem,1.2fr)]">'
        + regionMarkup(state, "layers")
        + regionMarkup(state, "operations")
        + '</div>'
        + regionMarkup(state, "advanced")
        + '</section>';
}
